import {
  IWorkflowBlueprint,
  IWorkflowRunError,
  IWorkflowStep,
  WORKFLOW_STEP_TYPES
} from '@care-portals/api-interfaces';

/* -------------------------------------------------------------------------- */
/*                              Error presentation                            */
/* -------------------------------------------------------------------------- */

export interface WorkflowRunErrorRow {
  label: string;
  value: string;
}

export interface WorkflowRunErrorPresentation {
  rows: WorkflowRunErrorRow[];
  /** When `error` is not JSON */
  fallbackText: string | null;
  /** Verbose `raw` from parsed errors — shown collapsed */
  technicalRaw: string | null;
  /** Only set for structured (post-migration) errors. */
  category?: 'internal' | 'adapter' | 'unexpected';
  confirmed?: boolean;
  badgeLabel?: string;
  badgeSubLabel?: string;
}

export const EMPTY_ERROR_PRESENTATION: WorkflowRunErrorPresentation = {
  rows: [],
  fallbackText: null,
  technicalRaw: null
};

type WorkflowRunErrorCategory = NonNullable<
  WorkflowRunErrorPresentation['category']
>;

const ERROR_BADGE_LABELS: Record<WorkflowRunErrorCategory, string> = {
  adapter: 'AdapterError',
  internal: 'InternalError',
  unexpected: 'UnexpectedError'
};

function parseRawError(raw?: string): Record<string, unknown> | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function stringifyTechnicalRaw(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string') {
    return value.trim() ? value : null;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function findStatusCode(value: unknown): unknown {
  if (typeof value === 'string') {
    try {
      return findStatusCode(JSON.parse(value) as unknown);
    } catch {
      return undefined;
    }
  }
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  if (isHttpStatusCode(record['statusCode'])) {
    return record['statusCode'];
  }
  if (isHttpStatusCode(record['status'])) {
    return record['status'];
  }

  return (
    findStatusCode(record['response']) ??
    findStatusCode(record['cause']) ??
    findStatusCode(record['raw'])
  );
}

function isHttpStatusCode(value: unknown): boolean {
  // Vendor payloads often carry an unrelated numeric `status` flag (0/1/2), so
  // only treat values in the real HTTP range as a status code.
  const numeric =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && /^\d{3}$/.test(value)
      ? Number(value)
      : NaN;
  return Number.isInteger(numeric) && numeric >= 100 && numeric <= 599;
}

/** Build presentation for the new structured IWorkflowRunError shape. */
function buildStructuredErrorPresentation(
  error: IWorkflowRunError
): WorkflowRunErrorPresentation {
  const raw = parseRawError(error.raw);
  const rows: WorkflowRunErrorRow[] = [];
  const statusCode = findStatusCode(raw);
  const fallbackText = cleanErrorText(error.displayMessage || error.message);

  if (error.source) {
    rows.push({ label: 'Source', value: error.source });
  }
  if (statusCode != null && String(statusCode) !== '') {
    rows.push({ label: 'Status code', value: String(statusCode) });
  }

  if (error.category === 'unexpected') {
    // CAR-2160: the API no longer redacts `message`/`raw`, so a differing
    // `message` is the real detail shown to every viewer.
    const hasRealMessage =
      typeof error.message === 'string' &&
      error.message.trim() &&
      error.message !== error.displayMessage;
    return {
      rows: hasRealMessage
        ? [
            ...rows,
            { label: 'Message', value: cleanErrorText(error.message) }
          ]
        : rows,
      fallbackText,
      technicalRaw: error.raw ?? null,
      category: 'unexpected',
      confirmed: error.confirmed,
      badgeLabel: ERROR_BADGE_LABELS.unexpected
    };
  }

  if (error.category === 'adapter') {
    return {
      rows,
      fallbackText,
      technicalRaw: error.raw ?? null,
      category: 'adapter',
      confirmed: error.confirmed,
      badgeLabel: ERROR_BADGE_LABELS.adapter
    };
  }

  // internal
  return {
    rows,
    fallbackText,
    technicalRaw: error.raw ?? null,
    category: 'internal',
    confirmed: error.confirmed,
    badgeLabel: ERROR_BADGE_LABELS.internal
  };
}

/**
 * Strip noisy `[object Object]` fragments (and any separator right before them)
 * that older runs baked into error strings via template-literal coercion of an
 * object, e.g. "…: beluga.createVisit, [object Object]" → "…: beluga.createVisit".
 * Also strips internal source/action prefixes like
 * "BondRx :: CreateOrder ::" or "PharmetikaAdapter :: createOrder() ::".
 * Falls back to the original text if stripping would leave nothing.
 */
export function cleanErrorText(text: string): string {
  if (typeof text !== 'string') {
    text = text == null ? '' : String(text);
  }
  const cleaned = text
    .replace(/\s*[,:]?\s*\[object Object\]/g, '')
    .replace(/^[A-Za-z][A-Za-z0-9]*\s*::\s*(?:[^:]+::\s*)*/u, '')
    .replace(/\s*::\s*$/u, '')
    .trim();
  return cleaned || text.trim();
}

function formatErrValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return cleanErrorText(String(value));
}

function splitMessageAndTrailingJson(message: string): {
  leadingText: string;
  nested?: Record<string, unknown>;
} {
  const normalized = message.replace(/\\n/g, '\n').trim();
  const braceIdx = normalized.lastIndexOf('{');
  if (braceIdx === -1) {
    return { leadingText: cleanErrorText(normalized) };
  }
  const jsonPart = normalized.slice(braceIdx).trim();
  try {
    const nested = JSON.parse(jsonPart) as unknown;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      let leading = normalized.slice(0, braceIdx).trim();
      leading = leading.replace(/\s*::\s*$/u, '').trim();
      return {
        leadingText: cleanErrorText(leading),
        nested: nested as Record<string, unknown>
      };
    }
  } catch {
    /* not JSON */
  }
  return { leadingText: cleanErrorText(normalized) };
}

function categoryFromValue(value: unknown): WorkflowRunErrorCategory | null {
  if (value !== 'adapter' && value !== 'internal' && value !== 'unexpected') {
    return null;
  }
  return value;
}

function categoryFromLegacyType(value: unknown): WorkflowRunErrorCategory | null {
  if (typeof value !== 'string') {
    return null;
  }
  if (/AdapterError/i.test(value)) {
    return 'adapter';
  }
  if (/InternalError/i.test(value)) {
    return 'internal';
  }
  if (/UnexpectedError/i.test(value)) {
    return 'unexpected';
  }
  return null;
}

function parseLegacyInvocationPrefix(message: string): {
  category: WorkflowRunErrorCategory;
  source?: string;
  message: string;
} | null {
  const match = message.match(
    /^Error invoking (adapter|middleware) function:\s*([A-Za-z0-9_-]+)(?:\.[^:]+)?\s*::\s*(.*)$/s
  );
  if (!match) {
    return null;
  }
  return {
    category: 'adapter',
    source: match[2],
    message: match[3]
  };
}

function legacyMessageFromObject(obj: Record<string, unknown>): {
  message: string;
  source?: string;
  category?: WorkflowRunErrorCategory;
} {
  const rawMessage =
    typeof obj['message'] === 'string'
      ? obj['message']
      : typeof obj['displayMessage'] === 'string'
      ? obj['displayMessage']
      : typeof obj['error'] === 'string'
      ? obj['error']
      : '';

  const invocation = parseLegacyInvocationPrefix(rawMessage);
  const message = invocation?.message ?? rawMessage;
  const { leadingText, nested } = splitMessageAndTrailingJson(message);
  const nestedMessage =
    nested &&
    (formatErrValue(nested['displayMessage']) ||
      formatErrValue(nested['message']) ||
      formatErrValue(nested['Message']) ||
      formatErrValue(nested['error']));

  return {
    message: cleanErrorText(nestedMessage || leadingText || rawMessage),
    source:
      (typeof obj['source'] === 'string' && obj['source']) ||
      invocation?.source ||
      undefined,
    category: invocation?.category
  };
}

function buildLegacyObjectErrorPresentation(
  obj: Record<string, unknown>
): WorkflowRunErrorPresentation {
  const rows: WorkflowRunErrorRow[] = [];
  const statusCode = findStatusCode(obj);
  const { message, source, category: invocationCategory } =
    legacyMessageFromObject(obj);
  const explicitCategory =
    categoryFromValue(obj['category']) ??
    categoryFromLegacyType(obj['errorType']) ??
    categoryFromLegacyType(obj['error']) ??
    invocationCategory;
  const category =
    explicitCategory ?? 'unexpected';
  const confirmed = explicitCategory != null;

  if (source) {
    rows.push({ label: 'Source', value: source });
  }
  if (statusCode != null && String(statusCode) !== '') {
    rows.push({ label: 'Status code', value: String(statusCode) });
  }

  const details = obj['details'];
  if (details != null && String(details).trim()) {
    rows.push({ label: 'Details', value: String(details) });
  }

  return {
    rows,
    fallbackText: message || null,
    technicalRaw: stringifyTechnicalRaw(obj['raw']),
    category,
    confirmed,
    badgeLabel: ERROR_BADGE_LABELS[category]
  };
}

/** Build row list for workflow run `error` (structured object, or legacy JSON-in-a-string from `parseError`). */
export function buildWorkflowRunErrorPresentation(
  error: string | IWorkflowRunError
): WorkflowRunErrorPresentation {
  if (typeof error === 'object' && error !== null && 'category' in error) {
    return buildStructuredErrorPresentation(error);
  }

  let obj: Record<string, unknown>;
  if (typeof error === 'object' && error !== null) {
    // A plain object without the `category` discriminator (e.g. a legacy error
    // persisted un-stringified). Use it directly — coercing it to a string and
    // JSON.parse()-ing would throw on "[object Object]", and the catch below
    // would then call cleanErrorText() on a non-string
    // ("replace is not a function").
    obj = error as unknown as Record<string, unknown>;
  } else {
    const errorStr = error as string;
    try {
      const parsed = JSON.parse(errorStr) as unknown;
      if (
        parsed === null ||
        typeof parsed !== 'object' ||
        Array.isArray(parsed)
      ) {
        return buildLegacyObjectErrorPresentation({
          message: typeof parsed === 'string' ? parsed : errorStr
        });
      }
      obj = parsed as Record<string, unknown>;
    } catch {
      return buildLegacyObjectErrorPresentation({ message: errorStr });
    }
  }

  return buildLegacyObjectErrorPresentation(obj);
}

/* -------------------------------------------------------------------------- */
/*                            Snapshot presentation                           */
/* -------------------------------------------------------------------------- */

export interface WorkflowSnapshotContextRow {
  label: string;
  value: string;
}

export interface WorkflowSnapshotStepView {
  type: string;
  typeLabel: string;
  title: string;
  contextRows: WorkflowSnapshotContextRow[];
  resultSummary: string | null;
  resultDetail: string | null;
  hasResultObject: boolean;
}

export interface WorkflowSnapshotPresentation {
  name: string | null;
  triggerEvent: string | null;
  enabled: boolean | null;
  executed: WorkflowSnapshotStepView[];
  pending: WorkflowSnapshotStepView[];
  pendingLabel: string;
}

const STEP_TYPE_LABELS: Record<string, string> = {
  [WORKFLOW_STEP_TYPES.trigger]: 'Trigger',
  [WORKFLOW_STEP_TYPES.adapter]: 'Adapter',
  [WORKFLOW_STEP_TYPES.crm_action]: 'CRM action',
  [WORKFLOW_STEP_TYPES.condition]: 'Condition',
  [WORKFLOW_STEP_TYPES.wait]: 'Wait',
  [WORKFLOW_STEP_TYPES.send_message]: 'Send message'
};

function stepTypeLabel(type: string): string {
  return STEP_TYPE_LABELS[type] ?? type.replace(/_/g, ' ');
}

function humanizeKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()
    .replace(/^\w/, c => c.toUpperCase());
}

function stepTitle(step: IWorkflowStep): string {
  const ctx = step.context ?? {};
  switch (step.type) {
    case WORKFLOW_STEP_TYPES.trigger:
      return String(ctx.selectedValue ?? 'Trigger');
    case WORKFLOW_STEP_TYPES.adapter:
      return [ctx.selectedAdapterName, ctx.selectedFunctionName]
        .filter(Boolean)
        .join(' · ');
    case WORKFLOW_STEP_TYPES.crm_action:
      return [ctx.selectedCrmActionName, ctx.selectedFunctionName]
        .filter(Boolean)
        .join(' · ');
    default:
      return step.type;
  }
}

function contextRowsForStep(step: IWorkflowStep): WorkflowSnapshotContextRow[] {
  const ctx = step.context ?? {};
  const rows: WorkflowSnapshotContextRow[] = [];

  if (step.type === WORKFLOW_STEP_TYPES.trigger) {
    return rows;
  }

  const params =
    ctx.selectedFunctionParams ?? ctx.selectedFunctionExtras ?? ctx.condition;
  if (params && typeof params === 'object' && !Array.isArray(params)) {
    for (const [k, v] of Object.entries(params)) {
      if (v === '' || v == null) {
        continue;
      }
      rows.push({ label: humanizeKey(k), value: String(v) });
    }
  }

  if (ctx.condition && step.type === WORKFLOW_STEP_TYPES.condition) {
    rows.push({
      label: 'Condition',
      value: JSON.stringify(ctx.condition)
    });
  }

  return rows;
}

function summarizeStepResult(result: unknown): {
  summary: string | null;
  detail: string | null;
  hasObject: boolean;
} {
  if (result === undefined || result === null) {
    return { summary: null, detail: null, hasObject: false };
  }
  if (typeof result === 'string') {
    return { summary: result, detail: null, hasObject: false };
  }
  if (typeof result !== 'object' || Array.isArray(result)) {
    return { summary: String(result), detail: null, hasObject: false };
  }

  const obj = result as Record<string, unknown>;
  const parts: string[] = [];

  if (obj.firstName != null || obj.lastName != null) {
    parts.push([obj.firstName, obj.lastName].filter(Boolean).join(' ').trim());
  }
  if (obj.email != null) {
    parts.push(String(obj.email));
  }
  if (obj._id != null) {
    parts.push(`ID ${obj._id}`);
  }
  if (obj.message != null) {
    parts.push(String(obj.message));
  }
  if (obj.error != null) {
    parts.push(String(obj.error));
  }

  const summary =
    parts.length > 0
      ? parts.join(' · ')
      : `Record (${Object.keys(obj).length} fields)`;

  return {
    summary,
    detail: JSON.stringify(result, null, 2),
    hasObject: true
  };
}

function stepToView(step: IWorkflowStep): WorkflowSnapshotStepView {
  const { summary, detail, hasObject } = summarizeStepResult(step.result);
  return {
    type: step.type,
    typeLabel: stepTypeLabel(step.type),
    title: stepTitle(step) || stepTypeLabel(step.type),
    contextRows: contextRowsForStep(step),
    resultSummary: summary,
    resultDetail: detail,
    hasResultObject: hasObject
  };
}

/** Build UI model for workflow run `snapshot` (blueprint + history + remaining steps). */
export function buildWorkflowSnapshotPresentation(
  snapshot: IWorkflowBlueprint | null | undefined,
  runStatus?: string
): WorkflowSnapshotPresentation {
  const empty: WorkflowSnapshotPresentation = {
    name: null,
    triggerEvent: null,
    enabled: null,
    executed: [],
    pending: [],
    pendingLabel: 'Remaining steps'
  };

  if (!snapshot) {
    return empty;
  }

  const history = snapshot.history ?? [];
  const steps = snapshot.steps ?? [];
  const failed = runStatus === 'failed';

  return {
    name: snapshot.name ?? null,
    triggerEvent: snapshot.triggerEvent ?? null,
    enabled: snapshot.enabled ?? null,
    executed: history.map(stepToView),
    pending: steps.map(stepToView),
    pendingLabel: failed ? 'Failed at this step' : 'Remaining steps'
  };
}
